import axios from "axios";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";

// Dronify's DSM service builds a PLY terrain surface from the LiDAR tile at a
// coordinate. This is intentionally a fallback: Google Solar remains the
// preferred (and immediate) source whenever it can produce a DSM.
export const DRONIFY_DSM_URL = "https://dsm.dronifyit.com";

const PLY_POLL_INTERVAL_MS = 5000;
const PLY_POLL_TIMEOUT_MS = 1500000; // 25 minutes for cold DSM jobs
const PLY_POST_TIMEOUT_MS = PLY_POLL_TIMEOUT_MS;
const PLY_STATUS_TIMEOUT_MS = 30000;
const PLY_DOWNLOAD_TIMEOUT_MS = 600000;
const PLY_POST_RETRY_LIMIT = 3;
const PLY_STATUS_TRANSIENT_RETRY_LIMIT = 5;
const PLY_MAX_POINTS = 160000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const noop = () => {};

const toFiniteNumber = (value, fallback = null) => {
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isTransientRequestError = (error) => {
  if (!error || error.response) return false;
  const code = String(error.code || "").toUpperCase();
  return (
    code === "ECONNABORTED" ||
    code === "ETIMEDOUT" ||
    code === "ERR_NETWORK" ||
    code === "ECONNRESET" ||
    !error.code
  );
};

// A missing stage means a cached/ready surface, so callers deliberately keep
// the loader hidden in that case.
const describeJobStage = (stageText) => {
  const text = String(stageText || "").toLowerCase();
  if (!text.trim()) return null;
  if (/(\.laz|\.las|url:|download|fetch|tile|point.?cloud)/.test(text)) {
    return "Downloading point cloud (LAZ)";
  }
  if (/(ept|entwine|index)/.test(text)) return "Building point index (EPT)";
  if (/(dsm|dem|raster|grid)/.test(text)) return "Generating DSM surface";
  if (/(ply|mesh|surface|export|writing|save)/.test(text)) return "Exporting surface (PLY)";
  return "Processing LiDAR";
};

const resolveStartedAtMs = (job) => {
  const started = toFiniteNumber(job?.started_at) ?? toFiniteNumber(job?.created_at);
  return started != null ? started * 1000 : Date.now();
};

const buildJobFileUrl = (job, jobId) => {
  const reported = typeof job?.file_url === "string" ? job.file_url.trim() : "";
  if (reported) return reported.startsWith("http") ? reported : `${DRONIFY_DSM_URL}${reported}`;
  return `${DRONIFY_DSM_URL}/jobs/${jobId}/file`;
};

const waitForPlyJob = async (jobId, { timeoutMs = PLY_POLL_TIMEOUT_MS, onProgress = noop } = {}) => {
  const deadline = Date.now() + timeoutMs;
  let consecutiveFailures = 0;
  let lastStatus = "pending";

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const response = await axios.get(`${DRONIFY_DSM_URL}/jobs/${jobId}`, {
        timeout: PLY_STATUS_TIMEOUT_MS,
      });
      consecutiveFailures = 0;
      const job = response?.data ?? null;
      const status = String(job?.status || "").toLowerCase();
      lastStatus = status || lastStatus;

      if (job?.error) throw new Error(`Dronify PLY job failed: ${job.error}`);
      if (status === "done") return job;
      if (status === "error" || status === "failed") {
        throw new Error(`Dronify PLY job ended with status "${status}".`);
      }

      const stageLabel = describeJobStage(job?.stage);
      if (stageLabel) {
        onProgress({ visible: true, stageLabel, startedAt: resolveStartedAtMs(job) });
      }
    } catch (error) {
      if (!isTransientRequestError(error)) throw error;
      consecutiveFailures += 1;
      if (consecutiveFailures > PLY_STATUS_TRANSIENT_RETRY_LIMIT) {
        throw new Error(`Dronify PLY status polling failed ${consecutiveFailures} times in a row: ${String(error?.message || error)}`);
      }
    }

    if (Date.now() > deadline) {
      throw new Error(`Dronify PLY job timed out after ${timeoutMs}ms (last status "${lastStatus}").`);
    }
    await sleep(PLY_POLL_INTERVAL_MS);
  }
};

const startPlyJob = async (lat, lon) => {
  let lastError = null;
  for (let attempt = 1; attempt <= PLY_POST_RETRY_LIMIT; attempt += 1) {
    try {
      const response = await axios.post(`${DRONIFY_DSM_URL}/ply`, { lat, lon }, { timeout: PLY_POST_TIMEOUT_MS });
      return response?.data ?? null;
    } catch (error) {
      lastError = error;
      if (!isTransientRequestError(error) || attempt === PLY_POST_RETRY_LIMIT) throw error;
      await sleep(PLY_POLL_INTERVAL_MS);
    }
  }
  throw lastError;
};

const decodePlyToPoints = (arrayBuffer) => {
  const geometry = new PLYLoader().parse(arrayBuffer);
  const position = geometry?.attributes?.position;
  if (!position?.count) {
    geometry?.dispose?.();
    return [];
  }

  const { array, count: total } = position;
  const stride = total > PLY_MAX_POINTS ? Math.ceil(total / PLY_MAX_POINTS) : 1;
  let sumX = 0;
  let sumY = 0;
  let sampled = 0;
  for (let i = 0; i < total; i += stride) {
    const x = array[i * 3];
    const y = array[i * 3 + 1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    sumX += x;
    sumY += y;
    sampled += 1;
  }
  if (!sampled) {
    geometry.dispose?.();
    return [];
  }

  const centerX = sumX / sampled;
  const centerY = sumY / sampled;
  const points = [];
  for (let i = 0; i < total; i += stride) {
    const x = array[i * 3];
    const y = array[i * 3 + 1];
    const z = array[i * 3 + 2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    points.push({ x: x - centerX, y: y - centerY, z });
  }
  geometry.dispose?.();
  return points;
};

export const fetchDronifyPlyLidar = async (
  latitude,
  longitude,
  { timeoutMs = PLY_POLL_TIMEOUT_MS, onProgress } = {}
) => {
  const report = typeof onProgress === "function" ? onProgress : noop;
  const finish = () => report({ visible: false, done: true });
  const lat = toFiniteNumber(latitude);
  const lon = toFiniteNumber(longitude);
  if (lat == null || lon == null) throw new Error("Missing latitude/longitude for Dronify PLY request.");

  try {
    const startJob = await startPlyJob(lat, lon);
    const jobId = startJob?.id;
    if (!jobId) throw new Error("Dronify PLY pipeline did not return a job id.");
    if (startJob?.error) throw new Error(`Dronify PLY job failed: ${startJob.error}`);

    const initialStage = describeJobStage(startJob?.stage);
    if (initialStage && String(startJob?.status || "").toLowerCase() !== "done") {
      report({ visible: true, stageLabel: initialStage, startedAt: resolveStartedAtMs(startJob) });
    }

    const finishedJob =
      String(startJob?.status || "").toLowerCase() === "done"
        ? startJob
        : await waitForPlyJob(jobId, { timeoutMs, onProgress: report });
    const fileUrl = buildJobFileUrl(finishedJob, jobId);
    const fileResponse = await axios.get(fileUrl, { responseType: "arraybuffer", timeout: PLY_DOWNLOAD_TIMEOUT_MS });
    if (!fileResponse?.data?.byteLength) throw new Error("Dronify PLY file response was empty.");

    const points = decodePlyToPoints(fileResponse.data);
    if (!points.length) throw new Error("Dronify PLY file contained no usable points.");
    return {
      points: [{ points }], lat, lon, source: "dronify-ply",
      raster: { validPointCount: points.length, sampledPointCount: points.length },
      dronifyPly: {
        jobId,
        coordKey: finishedJob?.coord_key || null,
        cached: Boolean(finishedJob?.cached),
        sizeBytes: toFiniteNumber(finishedJob?.size_bytes, null),
        fileUrl,
      },
    };
  } finally {
    finish();
  }
};
